import { createSlice, Draft, PayloadAction } from '@reduxjs/toolkit';
import {
    initialMarketDataState,
    MarketDataBarItem,
    MarketDataBars,
    MarketDataRefData,
    MarketDataState,
    MarketDataTickData,
    MarketDataTickItem,
} from './MarketDataState';
import omitUndefined from 'utils/omitUndefined';
import { StreamMessage } from 'utils/messages/StreamMessage';

export interface BarData {
    symbol: string;
    time: number;
    binSize: number;
    open?: number;
    close?: number;
    high?: number;
    low?: number;
    volume?: number;
}

export interface TickData {
    symbol: string;
    time: number;
    bid?: number;
    ask?: number;
    lastPrice?: number;
    volume?: number;
    high?: number;
    low?: number;
}

export const findBar = (
    bars: MarketDataBars[],
    symbol: string,
    binSize: number
) => bars.find((b) => b.symbol === symbol && b.binSize === binSize);

export const findTickData = (
    ticks: MarketDataTickData[],
    symbol: string | undefined
) => ticks.find((b) => b.symbol === symbol);

export const getOrAddBar = (
    state: MarketDataState,
    symbol: string,
    binSize: number
) => {
    let bar = findBar(state.bars, symbol, binSize);

    if (!bar) {
        bar = { symbol, binSize, bars: [], messages: [] };
        state.bars.push(bar);
    }

    return bar;
};

export const getOrAddTickData = (state: MarketDataState, symbol: string) => {
    let tickData = findTickData(state.ticks, symbol);

    if (!tickData) {
        tickData = { symbol, ticks: [], messages: [] };
        state.ticks.push(tickData);
    }

    return tickData;
};

const reduceBar = (
    state: Draft<MarketDataState>,
    symbol: string,
    binSize: number,
    bar: MarketDataBarItem
) => {
    const barData = getOrAddBar(state, symbol, binSize);

    const barItem = barData.bars.find((b) => b.time === bar.time);
    if (barItem) {
        barData.bars = [
            ...barData.bars.filter((b) => b.time !== bar.time),
            { ...barItem, ...omitUndefined(bar) },
        ];
    } else {
        barData.bars.push(bar);
    }
};

const reduceTickData = (
    state: Draft<MarketDataState>,
    symbol: string,
    tick: MarketDataTickItem
) => {
    const tickData = getOrAddTickData(state, symbol);

    const tickDataItem = tickData.ticks.find((b) => b.time === tick.time);
    if (tickDataItem) {
        tickData.ticks = [
            ...tickData.ticks.filter((b) => b.time !== tick.time),
            { ...tickDataItem, ...omitUndefined(tick) },
        ];
    } else {
        tickData.ticks.push(tick);
    }
};

const appendMessage = (messages: StreamMessage[], message: string) => {
    if (messages.find((a) => a.message === message)) {
        return;
    }
    messages.push({
        message,
    });
};

const marketDataSlice = createSlice({
    name: 'marketDataSlice',
    initialState: initialMarketDataState,
    reducers: {
        addBar: (state, action: PayloadAction<BarData>) => {
            const { symbol, binSize, time, open, close, high, low, volume } =
                action.payload;
            reduceBar(state, symbol, binSize, {
                time,
                open,
                close,
                high,
                low,
                volume,
            });
        },
        addMessage: (state, action: PayloadAction<string>) => {
            const alert = action.payload;
            appendMessage(state.messages, alert);
        },
        clearMessages: (state) => {
            state.messages = [];
        },
        addBarMessage: (
            state,
            action: PayloadAction<{
                symbol: string;
                binSize: number;
                message: string;
            }>
        ) => {
            const { symbol, binSize, message } = action.payload;
            const bar = getOrAddBar(state, symbol, binSize)!;
            appendMessage(bar.messages, message);
        },
        clearBarMessages: (
            state,
            action: PayloadAction<{
                symbol: string;
                binSize: number;
            }>
        ) => {
            const { symbol, binSize } = action.payload;
            getOrAddBar(state, symbol, binSize)!.messages = [];
        },
        addBars: (state, action: PayloadAction<Array<BarData>>) => {
            for (const message of action.payload) {
                const {
                    symbol,
                    time,
                    binSize,
                    open,
                    close,
                    high,
                    low,
                    volume,
                } = message;
                reduceBar(state, symbol, binSize, {
                    time,
                    open,
                    close,
                    high,
                    low,
                    volume,
                });
            }
        },

        setRefData: (
            state,
            action: PayloadAction<
                {
                    bbSymbol: string;
                } & MarketDataRefData
            >
        ) => {
            const {
                bbSymbol,
                startTradingHoursTimestamp,
                endTradingHoursTimestamp,
                averageVolume,
                pnc,
            } = action.payload;
            const current = state.refData[bbSymbol];
            if (
                !current ||
                current.startTradingHoursTimestamp < startTradingHoursTimestamp
            ) {
                state.refData[bbSymbol] = {
                    startTradingHoursTimestamp,
                    endTradingHoursTimestamp,
                    averageVolume,
                    pnc,
                };
            }
        },

        setIndigoMarketDataEnabled: (state, action: PayloadAction<boolean>) => {
            state.indigoMarketDataEnabled = action.payload;
        },

        setExternalMarketDataSource: (state, action: PayloadAction<string>) => {
            state.externalMarketDataSource = action.payload;
        },

        addTickMessage: (
            state,
            action: PayloadAction<{
                symbol: string;
                message: string;
            }>
        ) => {
            const { symbol, message } = action.payload;
            const tickData = getOrAddTickData(state, symbol)!;
            appendMessage(tickData.messages, message);
        },
        clearTickMessages: (
            state,
            action: PayloadAction<{
                symbol: string;
            }>
        ) => {
            const { symbol } = action.payload;
            getOrAddTickData(state, symbol)!.messages = [];
        },
        addTicks: (state, action: PayloadAction<Array<TickData>>) => {
            for (const message of action.payload) {
                const { symbol, time, bid, ask, lastPrice, volume, high, low } =
                    message;
                reduceTickData(state, symbol, {
                    time,
                    bid,
                    ask,
                    lastPrice,
                    volume,
                    high,
                    low,
                });
            }
        },
    },
});

export const {
    addMessage,
    clearMessages,
    addBarMessage,
    clearBarMessages,
    addBar,
    addBars,
    addTickMessage,
    clearTickMessages,
    addTicks,
    setExternalMarketDataSource,
    setRefData,
    setIndigoMarketDataEnabled,
} = marketDataSlice.actions;

export type SetIndigoMarketDataEnabled = ReturnType<
    typeof setIndigoMarketDataEnabled
>;

export const marketDataSliceReducer = marketDataSlice.reducer;
